/* Generic ListWizard — schema-driven 3-step wizard (Setup → Capture → Diagnostic).
 * Used to compactly build many lean wizards without duplicating boilerplate.
 *
 * Schema shape:
 * {
 *   key, label, completedLabel,
 *   setup: [{ key, label, type: text|textarea|number|date|select, options?, placeholder?, half? }],
 *   capture: {
 *     itemKey: "items",       // key in tool_data that holds the array
 *     itemLabel: "row",       // singular noun for "Add X" button
 *     columns: [{ key, label, type, options?, width? }],
 *     defaultRow: { ... },
 *   },
 *   diagnostic: ({ data }) => ({
 *     stats: [{ label, value, accent? }],
 *     headers: [string], rows: [[...]],
 *   }),
 * }
 */
import ToolWizard from "./ToolWizard";
import { Plus, Trash2, FileText } from "lucide-react";

function SetupForm({ schema, data, updateData, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`${testidPrefix}-setup`}>
      {schema.map(f => {
        const cls = f.half ? "" : "md:col-span-2";
        const value = data[f.key] ?? (f.type === "number" ? 0 : "");
        const set = (v) => updateData({ [f.key]: f.type === "number" ? Number(v) : v });
        return (
          <div key={f.key} className={cls}>
            <label className="block text-xs font-semibold text-sage-600 mb-1">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea data-testid={`${testidPrefix}-setup-${f.key}`} value={value} onChange={e => set(e.target.value)} placeholder={f.placeholder || ""} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm min-h-[60px]" />
            ) : f.type === "select" ? (
              <select data-testid={`${testidPrefix}-setup-${f.key}`} value={value} onChange={e => set(e.target.value)} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm bg-white">
                {(f.options || []).map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
              </select>
            ) : (
              <input data-testid={`${testidPrefix}-setup-${f.key}`} type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"} step={f.type === "number" ? "0.01" : undefined} value={value} onChange={e => set(e.target.value)} placeholder={f.placeholder || ""} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function CaptureList({ capture, data, updateData, testidPrefix }) {
  const items = data[capture.itemKey] || [];
  const add = () => updateData({ [capture.itemKey]: [...items, { ...capture.defaultRow, _id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }] });
  const upd = (i, p) => updateData({ [capture.itemKey]: items.map((it, idx) => idx === i ? { ...it, ...p } : it) });
  const rm  = (i) => updateData({ [capture.itemKey]: items.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-capture`}>
      {capture.helpText && <div className="bg-sage-50 rounded-md p-2 text-xs text-sage-600">{capture.helpText}</div>}
      <div className="hidden md:grid grid-cols-12 gap-2 px-2 py-1.5 bg-sage-100 rounded-md text-[10px] uppercase tracking-wider font-semibold text-sage-700 border border-sage-200" data-testid={`${testidPrefix}-headers`}>
        {capture.columns.map(col => {
          const w = col.width || `md:col-span-${col.span || 3}`;
          return (
            <div key={col.key} className={`${w} ${col.type === "number" ? "text-right pr-2" : ""}`} title={col.help || col.label}>
              {col.label}{col.required ? <span className="text-red-500">*</span> : null}
            </div>
          );
        })}
        <div className="md:col-span-1 text-right text-sage-400">×</div>
      </div>
      {items.map((it, i) => (
        <div key={it._id || i} className="bg-white border border-sage-200 rounded-md p-2 grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`${testidPrefix}-row-${i}`}>
          {capture.columns.map(col => {
            const w = col.width || `md:col-span-${col.span || 3}`;
            const v = it[col.key] ?? (col.type === "number" ? 0 : "");
            const set = (val) => upd(i, { [col.key]: col.type === "number" ? Number(val) : val });
            const ph = col.placeholder || col.label;
            // Mobile-only label so small screens still know which field is which
            const mobLabel = <span className="md:hidden text-[10px] font-semibold text-sage-500 uppercase tracking-wider">{col.label}</span>;
            if (col.type === "select") {
              return (
                <div key={col.key} className={`${w} flex flex-col gap-1`}>
                  {mobLabel}
                  <select data-testid={`${testidPrefix}-row-${i}-${col.key}`} value={v} onChange={e => set(e.target.value)} className="w-full border border-sage-200 rounded-md px-2 py-1.5 text-xs bg-white">{(col.options || []).map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}</select>
                </div>
              );
            }
            if (col.type === "textarea") {
              return (
                <div key={col.key} className={`${w} flex flex-col gap-1`}>
                  {mobLabel}
                  <textarea data-testid={`${testidPrefix}-row-${i}-${col.key}`} value={v} placeholder={ph} onChange={e => set(e.target.value)} className="w-full border border-sage-200 rounded-md px-2 py-1.5 text-xs min-h-[40px]" />
                </div>
              );
            }
            if (col.type === "checkbox") {
              return <label key={col.key} className={`${w} flex items-center gap-1 text-xs text-sage-600`}><input type="checkbox" checked={!!it[col.key]} onChange={e => upd(i, { [col.key]: e.target.checked })} /> <span className="md:hidden">{col.label}</span></label>;
            }
            return (
              <div key={col.key} className={`${w} flex flex-col gap-1`}>
                {mobLabel}
                <input data-testid={`${testidPrefix}-row-${i}-${col.key}`} type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"} value={v} placeholder={ph} onChange={e => set(e.target.value)} className={`w-full border border-sage-200 rounded-md px-2 py-1.5 text-xs ${col.type === "number" ? "font-mono text-right" : ""}`} />
              </div>
            );
          })}
          <button onClick={() => rm(i)} title="Remove row" className="text-red-500 justify-self-end md:col-span-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button data-testid={`${testidPrefix}-add`} onClick={add} className="flex items-center gap-1 border border-dashed border-sage-300 text-sage-600 rounded-md px-3 py-2 text-sm hover:bg-sage-50 w-full justify-center"><Plus className="w-3.5 h-3.5" /> Add {capture.itemLabel}</button>
    </div>
  );
}

export function DiagnosticView({ stats = [], headers = [], rows = [], extra = null, onExportPDF, data, testidPrefix }) {
  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-diagnostic`}>
      {stats.length > 0 && (
        <div className={`grid grid-cols-2 md:grid-cols-${Math.min(stats.length, 4)} gap-3`}>
          {stats.map((s, i) => (
            <div key={i} className={`rounded-md p-3 border ${s.accent === "danger" ? "bg-red-50 border-red-200" : s.accent === "warn" ? "bg-amber-50 border-amber-200" : s.accent === "good" ? "bg-green-50 border-green-200" : "bg-sage-50 border-sage-100"}`}>
              <p className="text-xs uppercase tracking-wider font-semibold text-sage-500">{s.label}</p>
              <p className="font-heading text-2xl font-bold text-sage-900">{s.value}</p>
            </div>
          ))}
        </div>
      )}
      {extra}
      {rows.length > 0 && headers.length > 0 && (
        <table className="w-full text-xs border border-sage-100 rounded-md overflow-hidden">
          <thead className="bg-sage-50 text-sage-600 uppercase tracking-wider"><tr>{headers.map((h, i) => <th key={i} className="text-left px-2 py-1.5">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (<tr key={i} className="border-t border-sage-100">{r.map((c, j) => <td key={j} className="px-2 py-1 text-sage-800">{c}</td>)}</tr>))}</tbody>
        </table>
      )}
      <button data-testid={`export-${testidPrefix}-pdf`} onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Export PDF</button>
    </div>
  );
}

export default function GenericListWizard({ schema, project, onSave, onExportPDF }) {
  const steps = [
    { key: "setup", label: schema.setupLabel || "Setup", render: ({ data, updateData }) => <SetupForm schema={schema.setup} data={data} updateData={updateData} testidPrefix={schema.key} /> },
    { key: "capture", label: schema.captureLabel || "Capture", render: ({ data, updateData }) => <CaptureList capture={schema.capture} data={data} updateData={updateData} testidPrefix={schema.key} /> },
    {
      key: "diagnostic", label: "Diagnostic",
      render: ({ data }) => {
        const d = schema.diagnostic({ data });
        return <DiagnosticView {...d} onExportPDF={onExportPDF} data={data} testidPrefix={schema.key} />;
      },
    },
  ];
  return <ToolWizard project={project} steps={steps} onSave={onSave} completedLabel={schema.completedLabel || "Finish"} />;
}
